import { useState } from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';

export default function StockDialog({ product, onOpenChange, onSuccess }) {
  const isConfirmed = product.stock?.status === 'confirmed';
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('Physical stock count');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (qty === '' || Number(qty) < 0 || Number.isNaN(Number(qty))) {
      toast.error('Enter a valid stock quantity (0 or more)');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/stock/set', { barcode: product.barcode, quantity: Number(qty), reason });
      toast.success(data.action === 'opening_stock'
        ? `Opening stock anchored at ${data.current_stock}`
        : `Stock adjusted: ${data.previous_stock} → ${data.current_stock}`);
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent data-testid="stock-dialog" className="rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>
            {isConfirmed ? 'Correct Stock Count' : 'Set Opening Stock'}
          </DialogTitle>
          <DialogDescription>
            <span className="font-medium text-zinc-700">{product.product_name}</span>
            {isConfirmed
              ? ' — enter the actual physical count. The difference is recorded as an adjustment (full audit trail kept).'
              : ' — enter the actual physical count. From this moment, stock is tracked exactly using sales and purchase uploads.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm bg-zinc-50 border border-zinc-200 px-3 py-2">
            <span className="text-zinc-500">{isConfirmed ? 'System says' : 'Current estimate'}</span>
            <span className={`font-mono font-bold ${product.stock?.value < 0 ? 'text-red-600' : 'text-zinc-900'}`}>
              {Number(product.stock?.value ?? 0).toLocaleString()}
              {!isConfirmed && <span className="text-[10px] text-zinc-400 font-sans ml-1">Est.</span>}
            </span>
          </div>
          {isConfirmed && product.stock?.anchored_at && (
            <p className="text-xs text-zinc-400">
              Anchored {format(new Date(product.stock.anchored_at), 'dd-MM-yyyy, HH:mm')} — opening stock {product.stock.opening_stock}
            </p>
          )}
          <div>
            <label className="field-label">Actual physical count</label>
            <Input
              data-testid="stock-qty-input"
              type="number"
              min="0"
              autoFocus
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="e.g. 115"
              className="rounded-none border-zinc-300 h-10 font-mono focus:border-emerald-600 focus-visible:ring-0"
            />
          </div>
          {isConfirmed && (
            <div>
              <label className="field-label">Reason</label>
              <Input
                data-testid="stock-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="rounded-none border-zinc-300 h-9 text-sm focus:border-emerald-600 focus-visible:ring-0"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} className="rounded-none border-zinc-300">
            Cancel
          </Button>
          <Button data-testid="stock-save-btn" onClick={save} disabled={saving} className="rounded-none bg-emerald-600 hover:bg-emerald-700">
            {saving ? 'Saving...' : isConfirmed ? 'Save Adjustment' : 'Confirm Opening Stock'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
